import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import './SearchByLaximoWithNameContainer.scss';
// @ts-check
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import SearchIcon from '@mui/icons-material/Search';
import LaximoVehicleContainer from "../../LaximoVehicleContainer/LaximoVehicleContainer";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import ReCAPTCHA from 'react-google-recaptcha';
import {API_URL, RECAPTCHA_SITE_KEY} from "../../../../config.tsx";
import {ArrowDropDown, ArrowDropUp, ContactSupportRounded} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";

// Константа для reCAPTCHA v2 (если нужна)
const RECAPTCHA_V2_SITE_KEY = RECAPTCHA_SITE_KEY;



/**
 * Компонент заголовка таблицы с характеристиками автомобиля
 * @param {Object} props - Пропсы компонента
 * @param {import('./SearchByLaximoWithNameContainer.d').VehicleAttribute[]} props.vehicleAttributes - Массив атрибутов автомобиля
 */
const VehicleHeader = ({ vehicleAttributes }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

	return (
		<TableRow>
			{vehicleAttributes.map(vehicleAttribute => (
				<TableCell
					key={vehicleAttribute.key}
					sx={{
						[theme.breakpoints.down('xs')]: {
							maxWidth: 50,
							fontSize: 11,
						},
					}}
				>
					{vehicleAttribute.name}
				</TableCell>
			))}
		</TableRow>
	)
}

/**
 * Компонент строки таблицы с характеристиками автомобиля
 * @param {Object} props - Пропсы компонента
 * @param {import('./SearchByLaximoWithNameContainer.d').Vehicle} props.vehicle - Данные автомобиля
 * @param {import('./SearchByLaximoWithNameContainer.d').VehicleAttribute[]} props.vehicleAttributes - Массив атрибутов автомобиля
 * @param {import('./SearchByLaximoWithNameContainer.d').FindVehicleFunction} props.findVehicle - Функция поиска автомобиля
 */
const VehicleRow = ({ vehicle, vehicleAttributes, findVehicle }) => {
	const [showAll, setShowAll] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

	return (
		<TableRow className="active-row">
			{
				vehicleAttributes.map(vehicleAttribute => {
					return (
						<TableCell
							key={vehicleAttribute.key}
							style={{padding: 5,}}
							sx={{
								[theme.breakpoints.down('xs')]: {
									maxWidth: 50,
									fontSize: 11,
								},
							}}
							onClick={() => findVehicle(vehicle.vehicleid, vehicle.catalog, vehicle.ssd)}
						>
							{showAll ? vehicleAttribute.value : vehicleAttribute.value.substring(0, 50)}
							{vehicleAttribute.value.length > 50 ? (
								showAll ?
									<div className="more-details-link" onClick={() => setShowAll(false)}>
										Скрыть
									</div> :
									<div className="more-details-link" onClick={() => setShowAll(true)}>
										Подробнее
									</div>
							) : null}
						</TableCell>
					)
				})
			}
		</TableRow>
	)
}

/**
 * Компонент для отображения детального изображения агрегата с интерактивными элементами
 * @param {Object} props - Пропсы компонента
 * @param {string} props.unitId - ID агрегата
 * @param {string} props.catalog - Каталог
 * @param {string} props.ssd - SSD параметр
 * @param {import('./SearchByLaximoWithNameContainer.d').CloseDialogFunction} props.closeDialog - Функция закрытия диалога
 * @param {string} props.brand - Бренд автомобиля
 * @param {string[]} props.oems - Массив OEM номеров
 */
const UnitListImage = ({ unitId, catalog, ssd, closeDialog, brand, oems }) => {
	/** @type {[import('./SearchByLaximoWithNameContainer.d').UnitDetail | null, React.Dispatch<React.SetStateAction<import('./SearchByLaximoWithNameContainer.d').UnitDetail | null>>]} */
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(true);

	const searchListDetailsByUnit = (unitId, catalog, ssd) => {
		if(!ssd && !unitId && !catalog) {
			setLoading(false);
			return;
		}
		fetch(`${API_URL}/list_details_by_unit?unitId=${unitId}&catalog=${catalog}&ssd=${ssd}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(detail => {
				if (Object.keys(detail).length) {
					setDetail(detail);
				}
				setLoading(false);
			})
	};

	useEffect(() => {
		searchListDetailsByUnit(unitId, catalog, ssd);
	}, [unitId])

	return (
		<Dialog open={true} onClose={() => closeDialog()}>
			<DialogContent>
				{loading ? (
					<div className="loading-container">
						<CircularProgress size={60} />
					</div>
				) : null}
				{
					detail
						? <div className="unit-image">
							<div className="unit-image-scaled" style={{transform: 'scale(' + detail.scaleValue + ')'}}>
								{
									detail.detailsCoordinates
									? detail.detailsCoordinates.map(coordinates => {
										if (!coordinates.oem) return null;

										return (
											<div
												className={`detail-coordinates ${oems.includes(coordinates.oem) ? 'highlighted' : ''}`}
												style={{
													top: (coordinates.y1 - 4),
													left: (coordinates.x1 - 4),
												}}
												onClick={() => window.open(`/spares/${brand.toLowerCase()}/${coordinates.oem}`, '_blank')}
												key={`${coordinates.x1}-${coordinates.y1}`}
											>
												<Tooltip
													title={
														<div>
															<div><strong>OEM: </strong>{coordinates.oem}</div>
															<div><strong>Наименование: </strong>{coordinates.name}</div>
														</div>
													}
													placement="top-end"
													disableTouchListener
													componentsProps={{
														tooltip: {
															sx: {
																background: 'white',
																color: 'text.primary',
																boxShadow: 1,
																fontSize: 14,
																minWidth: 500,
															}
														},
														popper: {
															sx: {
																opacity: 1,
																zIndex: 1301,
															}
														}
													}}
												>
													<div
														style={{
															width: (coordinates.x2 - coordinates.x1 + 12),
															height: (coordinates.y2 - coordinates.y1 + 12)
														}}
													>
														{oems.includes(coordinates.oem) ? coordinates.oem : ' '}
													</div>
												</Tooltip>
											</div>
										);
									})
									: null
								}
							</div>
							<img src={detail.imageUrl} style={{width: 600,}}/>
						</div>
						: null
				}
			</DialogContent>
		</Dialog>
	);
};

/**
 * Компонент спецификации автомобиля
 * @param {Object} props - Пропсы компонента
 * @param {import('./SearchByLaximoWithNameContainer.d').FindVehicleFunction} props.findVehicle - Функция поиска автомобиля
 * @param {import('./SearchByLaximoWithNameContainer.d').SearchVehicle} props.searchVehicle - Данные поиска автомобиля
 * @param {boolean} props.isMobile - Флаг мобильного устройства
 */
const Specification = ({ findVehicle, searchVehicle, isMobile }) => {
	const [expanded, setExpanded] = useState(!isMobile);

	useEffect(() => {
		setExpanded(!isMobile);
	}, [isMobile]);

	return (
		<Accordion
			expanded={expanded}
			onChange={() => setExpanded(!expanded)}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon/>}
			>
				<Typography variant="h6" style={{fontSize: 16}}>
					Оригинальная спецификация
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Table>
					<TableBody>
						{
							searchVehicle.headers.map(vehicleAttribute => {
								return (
									<TableRow
										key={vehicleAttribute.key}
										className="active-row"
									>
										<TableCell
											sx={{
												[theme.breakpoints.down('xs')]: {
													maxWidth: 50,
													fontSize: 11,
												},
											}}
										>
											{vehicleAttribute.name}
										</TableCell>
										<TableCell
											style={{padding: 5,}}
											sx={{
												[theme.breakpoints.down('xs')]: {
													maxWidth: 50,
													fontSize: 11,
												},
											}}
											onClick={() => findVehicle(searchVehicle.vehicle.vehicleid, searchVehicle.vehicle.catalog, searchVehicle.vehicle.ssd)}
										>
											{vehicleAttribute.value}
										</TableCell>
									</TableRow>
								)
							})
						}
					</TableBody>
				</Table>
			</AccordionDetails>
		</Accordion>
	);
};

/**
 * Компонент изображения агрегата
 * @param {Object} props - Пропсы компонента
 * @param {import('./SearchByLaximoWithNameContainer.d').Unit} props.unit - Данные агрегата
 * @param {string} props.catalog - Каталог
 * @param {string} props.brand - Бренд автомобиля
 */
const UnitImage = ({ unit, catalog, brand }) => {
	const [showImage, setShowImage] = useState(false);
	const imageUrl = unit.imageUrl ? unit.imageUrl : unit.imageurl;
	if (!imageUrl) return null;

	let oems = [];

	unit.details.map(detail => {
		oems.push(detail.oem);
	})

	return (
		<Card className="card-styles">
			{
				showImage
					? <UnitListImage
						catalog={catalog}
						brand={brand}
						oems={oems}
						unitId={unit.unitId}
						ssd={unit.ssd}
						closeDialog={() => setShowImage(false)}
					/>
					: null
			}
			<CardActionArea
				className="action-area-styles"
				onClick={() => setShowImage(true)}
			>
				<CardMedia
					className="media-styles"
					image={imageUrl.replace(/%size%/, 250)}
					title={unit.name}
				/>
				<Typography variant="caption" style={{justifyContent: "center", marginTop: 24, display: 'flex'}}>
					{unit.name}
				</Typography>
			</CardActionArea>
		</Card>
	);
};

/**
 * Компонент агрегата с деталями
 * @param {Object} props - Пропсы компонента
 * @param {import('./SearchByLaximoWithNameContainer.d').Unit} props.unit - Данные агрегата
 * @param {string} props.catalog - Каталог
 * @param {string} props.brand - Бренд автомобиля
 */
const Unit = ({ unit, catalog, brand }) => {
	const [showAll, setShowAll] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

	return (
		<Grid container key={unit.unitId} className="units-line">
			<Grid item xs={12} md={4}>
				<UnitImage key={'unit-' + unit.unitId} unit={unit} catalog={catalog} brand={brand}/>
			</Grid>
			<Grid item xs={12} md={8}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell
								sx={{
									[theme.breakpoints.down('xs')]: {
										maxWidth: 50,
										fontSize: 11,
									},
								}}
							>
								OEM
							</TableCell>
							<TableCell
								sx={{
									[theme.breakpoints.down('xs')]: {
										maxWidth: 50,
										fontSize: 11,
									},
								}}
							>
								Наименование
							</TableCell>
							{!isMobile && <TableCell
								sx={{
									[theme.breakpoints.down('xs')]: {
										maxWidth: 50,
										fontSize: 11,
									},
								}}
							>
								Действия
							</TableCell>}
							{isMobile && <TableCell
								sx={{
									[theme.breakpoints.down('xs')]: {
										maxWidth: 50,
										fontSize: 11,
									},
								}}
							></TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{
							unit.details.map((detail, key) => {
								if(!showAll && key > 3) return null;

								return (
									<TableRow
										key={detail.ssd}
										className="detail-row"
									>
										<TableCell
											sx={{
												padding: 2,
												textAlign: 'center',
												[theme.breakpoints.down('xs')]: {
													fontSize: 11,
													padding: '5px!important',
													textAlign: 'center',
												},
											}}
										>
											<div style={{display: "flex", alignItems: "center"}}>
												<a target="_blank"
												   href={`/spares/${brand}/${detail.oem}`}>{detail.oem}</a>
												{!isMobile && (
													<Tooltip
														placement="top-end"
														componentsProps={{
															tooltip: {
																sx: {
																	background: 'white',
																	color: 'text.primary',
																	boxShadow: 1,
																	fontSize: 14,
																	minWidth: 300,
																}
															},
															popper: {
																sx: {
																	opacity: 1
																}
															}
														}}
														title={
															<div style={{width: 300}}>
																<div><strong>OEM: </strong>{detail.oem}</div>
																<div><strong>Наименование: </strong>{detail.name}</div>
																<div><strong>Количество: </strong>{detail.amount}</div>
																{detail.applicableModels ? (
																	<div><strong>Применяется в моделях: </strong>{detail.applicableModels}</div>
																) : null}
															</div>
														}
													>
														<IconButton
															style={{marginLeft: 12,}}
															sx={{ padding: 0 }}
														>
															<ContactSupportRounded/>
														</IconButton>
													</Tooltip>
												)}
											</div>
										</TableCell>
										<TableCell
											sx={{
												padding: 2,
												[theme.breakpoints.down('xs')]: {
													fontSize: 11,
													padding: '5px!important',
													textAlign: 'center',
												},
											}}
										>
											<a target="_blank" href={`/spares/${brand}/${detail.oem}`}>{detail.name}</a>
										</TableCell>
										{!isMobile && (
											<TableCell
												sx={{
													padding: 2,
													textAlign: 'center',
													position: 'relative',
													minWidth: 100,
													[theme.breakpoints.down('xs')]: {
														fontSize: 11,
														padding: '5px!important',
														textAlign: 'center',
													},
												}}
											>
												<Button
													target="_blank"
													href={`/spares/${brand}/${detail.oem}`}
                                                    className="show-price-btn"
													style={{fontSize: 12, color: 'rgba(0, 0, 0, 0.87)'}}
												>
													узнать цены
												</Button>
											</TableCell>
										)}
										{isMobile && (
											<TableCell
												sx={{
													padding: 8,
													textAlign: 'center',
													[theme.breakpoints.down('xs')]: {
														fontSize: 11,
														padding: '5px!important',
														textAlign: 'center',
													},
												}}
											>
												<Button
													color="primary"
													target="_blank"
													href={`/spares/${brand}/${detail.oem}`}
												>
													<SearchIcon />
												</Button>
											</TableCell>
										)}
									</TableRow>
								);
							})
						}
						{
							unit.details.length > 4
								? (
									!showAll
										? <TableRow><TableCell colSpan={3}><Button style={{ width: '100%' }} onClick={() => setShowAll(true)}><KeyboardArrowDown style={{marginRight: 20}} /> Показать все</Button></TableCell></TableRow>
										: <TableRow><TableCell colSpan={3}><Button style={{ width: '100%' }} onClick={() => setShowAll(false)}><KeyboardArrowUp style={{marginRight: 20}} /> Скрыть</Button></TableCell></TableRow>
								)
								: ''
						}
					</TableBody>
				</Table>
			</Grid>
		</Grid>
	)
}

/**
 * Основной компонент поиска автомобиля по каталогу Laximo с названием детали
 */
const SearchByLaximoWithNameContainer = () => {
	const url = new URL(window.location.href);
	const vinParams = url.searchParams.get('vin');
	const carNumberParams = url.searchParams.get('carNumber');
	const carNumberRegionParams = url.searchParams.get('carNumberRegion');
	const ssdCatalogParam = url.searchParams.get('catalogSsd');
	const vinOrCarNumberParam = url.searchParams.get('vinOrCarNumber');
	const brand = url.searchParams.get('brand');
	const partsNameParam = url.searchParams.get('partName');
	/** @type {[import('./SearchByLaximoWithNameContainer.d').SearchVehicle | null, React.Dispatch<React.SetStateAction<import('./SearchByLaximoWithNameContainer.d').SearchVehicle | null>>]} */
	const [searchVehicle, setSearchVehicle] = useState(null);
	/** @type {[any | null, React.Dispatch<React.SetStateAction<any | null>>]} */
	const [showVehicle, setShowVehicle] = useState(null);
	const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
	const [showSpecification, setShowSpecification] = useState(false);
	/** @type {React.RefObject<any>} */
	const recaptchaRef = useRef(null);
	/** @type {React.RefObject<any>} */
	const recaptchaV2Ref = useRef(null);
	/** @type {React.RefObject<(() => void) | null>} */
	const searchActionRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [emptyVehicle, setEmptyVehicle] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
	const navigate = useNavigate();

	/**
	 * Поиск автомобиля по VIN номеру
	 * @param {string} searchVIN - VIN номер для поиска
	 */
	const findSearchVehicle = (searchVIN) => {
		setSearchVehicle(null);
		if (!searchVIN) return;
		fetch(`${API_URL}/find_by_vin_with_name?vin=${searchVIN}&partName=${partsNameParam}&partName=${partsNameParam}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(vehicle => {
				setLoading(false);
				if(Object.keys(vehicle).length && vehicle.units && Object.keys(vehicle.units).length) setSearchVehicle(vehicle);
				else setEmptyVehicle(true);
			})
			.then(() => navigate(`/search-vehicle-with-name?vin=${searchVIN}&partName=${partsNameParam}`))
	};

	/**
	 * Поиск автомобиля по государственному номеру
	 * @param {string} carNumber - Номер автомобиля
	 * @param {string} carNumberRegion - Регион номера
	 */
	const findSearchVehicleByCarNumber = (carNumber, carNumberRegion) => {
		setSearchVehicle(null);
		fetch(`${API_URL}/find_by_car_number_with_name?number=${carNumber}&region=${carNumberRegion}&partName=${partsNameParam}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(vehicle => {
				setLoading(false);
				if(Object.keys(vehicle).length && vehicle.units && Object.keys(vehicle.units).length) setSearchVehicle(vehicle);
				else setEmptyVehicle(true);
			})
			.then(() => navigate(`/search-vehicle-with-name?carNumber=${carNumber}&carNumberRegion=${carNumberRegion}&partName=${partsNameParam}`))
	};

	/**
	 * Поиск автомобиля по SSD параметру каталога
	 * @param {string} ssd - SSD параметр
	 * @param {string} brand - Бренд автомобиля
	 */
	const findSearchVehicleByssdCatalogParam = (ssd, brand) => {
		setSearchVehicle(null);
		fetch(`${API_URL}/find_by_ssd_with_name?catalogBrand=${brand}&catalogSsd=${ssd}&partName=${partsNameParam}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(vehicle => {
				setLoading(false);
				if(Object.keys(vehicle).length && vehicle.units && Object.keys(vehicle.units).length) setSearchVehicle(vehicle);
				else setEmptyVehicle(true);
			})
	};

	/**
	 * Поиск автомобиля по VIN или номеру
	 * @param {string} vinOrCarNumber - VIN или номер автомобиля
	 */
	const findSearchVehicleByVinOrCarNumber = (vinOrCarNumber) => {
		setSearchVehicle(null);
		fetch(`${API_URL}/find_by_vin_or_car_number_with_name?vinOrCarNumber=${vinOrCarNumber}&partName=${partsNameParam}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(vehicle => {
				setLoading(false);
				if(Object.keys(vehicle).length && vehicle.units && Object.keys(vehicle.units).length) setSearchVehicle(vehicle);
				else setEmptyVehicle(true);
			})
	};

	/**
	 * Поиск детальной информации об автомобиле
	 * @param {string} vehicleId - ID автомобиля
	 * @param {string} brand - Бренд автомобиля
	 * @param {string} ssd - SSD параметр
	 */
	const findVehicle = (vehicleId, brand, ssd) => {
		setShowVehicle(null);
		fetch(`${API_URL}/find_by_vehicle_id?vehicleId=${vehicleId}&catalog=${brand}&ssd=${ssd}`, {
			method: 'GET',
			credentials: 'include',
		})
			.then(response => response.json())
			.then(data => setShowVehicle(data))
	};

	/**
	 * Обработка верификации reCAPTCHA
	 * @param {string} token - Токен reCAPTCHA
	 */
	const handleRecaptchaVerify = (token) => {
		fetch('/verify-recaptcha', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ token })
		})
			.then(response => response.json())
			.then(data => {
				if (data.success) {
					if (searchActionRef.current) {
						searchActionRef.current();
					}
				} else {
					setShowRecaptchaV2(true);
				}
			})
			.catch(error => {
				alert('Verification failed. Please try again.');
			});
	};

	/**
	 * Обработка верификации reCAPTCHA v2
	 * @param {string} token - Токен reCAPTCHA v2
	 */
	const handleRecaptchaV2Verify = (token) => {
		fetch('/verify-recaptcha', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({ token, action: 'recaptcha_v2' })
		})
			.then(response => response.json())
			.then(data => {
				if (data.success) {
					setShowRecaptchaV2(false);
					if (searchActionRef.current) {
						searchActionRef.current();
					}
				} else {
					alert('reCAPTCHA v2 verification failed');
				}
			});
	};

	useEffect(() => {
		if(vinParams) findSearchVehicle(vinParams);
		else if(carNumberParams && carNumberRegionParams) findSearchVehicleByCarNumber(carNumberParams, carNumberRegionParams);
		else if(ssdCatalogParam && brand) findSearchVehicleByssdCatalogParam(ssdCatalogParam, brand);
		else if(vinOrCarNumberParam) findSearchVehicleByVinOrCarNumber(vinOrCarNumberParam);
		else setLoading(false);
	}, []);

	if(showVehicle) return (
		<Grid container>
			<Grid item xs={12}>
				<LaximoVehicleContainer vehicle={showVehicle}/>
			</Grid>
		</Grid>
	);

	let goodData = '';
	if(vinParams) goodData = `(VIN: ${vinParams})`;
	else if(carNumberParams && carNumberRegionParams) goodData = `(госномер: ${carNumberParams} ${carNumberRegionParams})`;

	return (
		<div>
			<div className={showRecaptchaV2 ? 'blurred' : ''}>
				<div className="page-container">
					{loading ? <LinearProgress /> : null}
					{
						emptyVehicle
						? <div className="empty-state">
								<Typography variant="h6">
									По указанным данным автомобиля {goodData} информация о товаре "{partsNameParam.toLocaleLowerCase()}" не найдена
								</Typography>
								<Grid container>
									<Grid item xs={12} lg={2} className="help-buttons">
										<Button color="primary" variant="outlined" onClick={() => window.jivo_api.open()}>
											Помощь в подборе
										</Button>
									</Grid>
									<Grid item xs={12} lg={2} className="help-buttons">
										<Button color="primary" variant="contained" href="/?tab=2">
											Перейти в каталог
										</Button>
									</Grid>
								</Grid>
							</div>
							: null
					}
					{
						searchVehicle && searchVehicle.headers.length
							? <div>
								<div className="units-container">
									<Typography variant="h6" style={{padding: '12px'}}>Поиск по каталогу</Typography>
								</div>
								<Grid container>
									<Grid item xs={12}>
										<Typography
											variant="h6"
											className="vehicle-info"
										>
											<div style={{paddingLeft: '16px'}}>{searchVehicle.vehicle.brand} {searchVehicle.vehicle.name}</div>
											{vinParams ? (
												<div className="vehicle-details">
													{vinParams}
												</div>
											) : null}
											{carNumberParams && carNumberRegionParams ? (
												<div className="vehicle-details">
													{carNumberParams} {carNumberRegionParams}
												</div>
											) : null}
										</Typography>
										<div className="units-container">
											<div className="vehicle-characteristics">
												Характеристики авто {showSpecification ?
													<ArrowDropDown
														className="characteristics-toggle"
														onClick={() => setShowSpecification(false)}
													/> :
													<ArrowDropUp
														className="characteristics-toggle"
														onClick={() => setShowSpecification(true)}
													/>
												}
											</div>
											<div style={{display: showSpecification ? 'block' : 'none'}}>
												{!isMobile ? (
													<Table>
														<TableHead>
															<VehicleHeader
																vehicleAttributes={searchVehicle.headers}
															/>
														</TableHead>
														<TableBody>
															<VehicleRow
																vehicleAttributes={searchVehicle.headers}
																vehicle={searchVehicle.vehicle}
																findVehicle={findVehicle}
															/>
														</TableBody>
													</Table>
												) : (
													<Specification
														searchVehicle={searchVehicle}
														findVehicle={findVehicle}
														isMobile={isMobile}
													/>
												)}
											</div>
										</div>
									</Grid>
								</Grid>
							</div>
							: null
					}
					<Grid container className="units-container" style={{width: '100%'}}>
						{
							searchVehicle && Object.keys(searchVehicle).length
								? <Grid container style={{width: '100%'}}>
									<Grid item xs={12} className="units-items">
										<div>
											{
												searchVehicle.units.map(unit => (
													<Unit
														key={unit.unitId}
														unit={unit}
														catalog={searchVehicle.catalog}
														brand={searchVehicle.vehicle.brand}
													/>
												))
											}
										</div>
									</Grid>
									<Grid item xs={12} className="open-catalog-button">
										<Button
											color="primary"
											variant="contained"
											onClick={() => findVehicle(searchVehicle.vehicle.vehicleid, searchVehicle.vehicle.catalog, searchVehicle.vehicle.ssd)}>
											Открыть каталог
										</Button>
									</Grid>
								</Grid>
								: null
						}
					</Grid>
					<ReCAPTCHA
						ref={recaptchaRef}
						sitekey={RECAPTCHA_SITE_KEY}
						size="invisible"
						onChange={handleRecaptchaVerify}
					/>
				</div>
			</div>
			{showRecaptchaV2 && (
				<div className="recaptcha-overlay-styles">
					<div className="recaptcha-container-styles">
						<ReCAPTCHA
							ref={recaptchaV2Ref}
							sitekey={RECAPTCHA_V2_SITE_KEY}
							size="normal"
							onChange={handleRecaptchaV2Verify}
						/>
					</div>
				</div>
			)}
		</div>
	)
}

export default SearchByLaximoWithNameContainer;
